'use client';

import React, { useEffect, useState } from 'react';
import type { ConsentState } from './types';
import { readConsentCookie } from './consent-cookie';
import { useConsent, CONSENT_VERSION, POLICY_VERSION } from './useConsent';

interface CookieBannerProps {
  onManagePreferences?: () => void;
  privacyPolicyUrl?: string;
}

function buildAllConsent(accepted: boolean): ConsentState {
  // W2.2 fix (S9.A.01 Finding 12): stamp policy_version on the cookie payload
  // so a policy-change re-prompt can compare stored vs current.
  return {
    version: CONSENT_VERSION,
    policy_version: POLICY_VERSION,
    timestamp: new Date().toISOString(),
    categories: {
      necessary: true,
      functional: accepted,
      analytics: accepted,
      marketing: accepted,
    },
    source: 'user',
  };
}

export function CookieBanner({ onManagePreferences, privacyPolicyUrl = '/privacy' }: CookieBannerProps): React.JSX.Element | null {
  if (typeof window === 'undefined') return null;

  return <CookieBannerClient onManagePreferences={onManagePreferences} privacyPolicyUrl={privacyPolicyUrl} />;
}

function CookieBannerClient({ onManagePreferences, privacyPolicyUrl = '/privacy' }: CookieBannerProps): React.JSX.Element | null {
  const [visible, setVisible] = useState(false);
  const { setConsent } = useConsent();

  useEffect(() => {
    const stored = readConsentCookie();
    // W2.2 fix (S9.A.01 Finding 12): re-prompt when cookie schema OR policy
    // version is stale. Also, when consent was set automatically by the GPC
    // path or a withdrawal sentinel, don't re-show the banner — those sources
    // represent a final state per CCPA + GDPR Art. 7(3) right-to-withdraw.
    const stale =
      !stored ||
      stored.version !== CONSENT_VERSION ||
      stored.policy_version !== POLICY_VERSION;
    const finalState = stored?.source === 'gpc' || stored?.source === 'withdrawn';
    if (stale && !finalState) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const handleAcceptAll = async (): Promise<void> => {
    await setConsent(buildAllConsent(true));
    setVisible(false);
  };

  const handleRejectNonEssential = async (): Promise<void> => {
    await setConsent(buildAllConsent(false));
    setVisible(false);
  };

  const handleManage = (): void => {
    onManagePreferences?.();
  };

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed bottom-4 right-4 left-4 z-50 w-auto rounded-xl border border-white/15 bg-[#0e1626] p-4 shadow-2xl backdrop-blur-md sm:left-auto sm:w-[22rem]"
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm leading-relaxed text-foreground">
          We use cookies to improve your experience. Necessary cookies are always active.{' '}
          <a href={privacyPolicyUrl} className="underline hover:no-underline">
            Privacy Policy
          </a>
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleAcceptAll}
            className="w-full rounded-md bg-[#b22234] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#d4313f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Accept all
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleRejectNonEssential}
              className="flex-1 rounded-md border border-white/20 bg-transparent px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={handleManage}
              className="flex-1 rounded-md border border-white/20 bg-transparent px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Manage
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
